import CancelIcon from '@mui/icons-material/CancelOutlined'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { Box, Stack, Typography, useTheme } from '@mui/material'
import { FC, memo, useCallback, useEffect, useMemo, useRef } from 'react'
import HintIcon from '../../../assets/icons/hint.svg?react'
import {
    getLastPosition,
    getNextPosition,
    getPositionById,
    getPreviousPosition,
    getStartingPosition,
} from '../../../chess/gameTree'
import { GameTree, GameTreePosition } from '../../../chess/types'
import useCorrectPieceColor from '../../../hooks/useCorrectPieceColor'
import { piecesProvider, PiecesTheme } from '../../../sharedComponents/src/globalHeader/common/PiecesProvider'
import CircularButton from '../../analysisView/components/analyisControls/CircularButton'
import BackIcon from '../icons/Back'
import FirstIcon from '../icons/First'
import LastIcon from '../icons/Last'
import NextIcon from '../icons/Next'

// Move pieces provider outside to avoid recalculation
const pieces = piecesProvider(PiecesTheme.DEFAULT)

export type NotationProps = {
    notations: NotationsType[]
    gameTree: GameTree
    currentPosition: GameTreePosition
    setCurrentPosition: (nextPosition: GameTreePosition) => void
    disabled: boolean
}

export type NotationsType = {
    location: string
    id: string
    piece: string
    playersMove: boolean
    usedHint: boolean
    usedShowSolution: boolean
    isLost: boolean
    isCorrect: boolean
    nextPositionId: string
}

type NotationItemProps = {
    notation: NotationsType
}

const NotationItem: FC<NotationItemProps> = memo(({ notation }) => {
    const theme = useTheme()

    const iconAndLabel = useMemo(() => {
        if (notation.isLost) return { icon: <CancelIcon />, label: 'Incorrect' }
        if (notation.usedHint) return { icon: <HintIcon />, label: 'Hint' }
        if (notation.isCorrect) return { icon: <CheckCircleIcon />, label: 'Correct' }
        return null
    }, [notation.isLost, notation.usedHint, notation.isCorrect])

    const locationColor = notation.usedShowSolution || notation.usedHint ? theme.palette.glow.main : 'inherit'

    return (
        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Box component="span" sx={{ color: locationColor }}>
                {notation.location}
            </Box>
            {iconAndLabel && (
                <>
                    <Box component="span">:</Box>
                    {iconAndLabel.icon}
                    <Box component="span">{iconAndLabel.label}</Box>
                </>
            )}
        </Typography>
    )
})
NotationItem.displayName = 'NotationItem'

type MoveNumberProps = {
    number: number
}

const MoveNumber: FC<MoveNumberProps> = memo(({ number }) =>
    number > 1 ? (
        <Typography lineHeight="48px" fontSize="16px" variant="body2">
            {number - 1}.
        </Typography>
    ) : null,
)
MoveNumber.displayName = 'MoveNumber'

type MoveItemProps = {
    move: NotationsType
    isActive: boolean
    disabled: boolean
    onClick: () => void
}

const MoveItem: FC<MoveItemProps> = memo(({ move, isActive, disabled, onClick }) => {
    const theme = useTheme()
    const pieceColor = move.usedShowSolution ? theme.palette.glow.main : move.piece[0] === 'b' ? 'black' : ''
    const pieceStroke = move.piece[0] === 'b' && !move.usedShowSolution ? 'white' : ''

    const iconColor = useMemo(() => {
        if (move.isLost) return theme.palette.error.main
        if (move.isCorrect && !move.usedHint) return theme.palette.success.main
        return ''
    }, [move.isLost, move.isCorrect, move.usedHint])

    return (
        <Box
            onClick={onClick}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                height: '48px',
                padding: '4px',
                borderRadius: '0.25rem',
                cursor: disabled ? 'default' : 'pointer',
                backgroundColor: isActive ? 'background.default' : 'transparent',
                border: `1px solid ${isActive ? 'white' : 'transparent'} !important`,
                ':hover': {
                    backgroundColor: disabled ? 'transparent' : 'background.paper',
                },
            }}
        >
            <Box width="32px" height="32px" sx={useCorrectPieceColor(pieceColor, pieceStroke)}>
                {pieces[move.piece]?.({ isDragging: false, squareWidth: '100%' })}
            </Box>
            <Box
                sx={{
                    userSelect: 'none',
                    svg: {
                        width: '30px',
                        height: '30px',
                        margin: '0 0.25rem',
                        '> path': {
                            fill: iconColor,
                        },
                    },
                }}
            >
                <NotationItem notation={move} />
            </Box>
        </Box>
    )
})
MoveItem.displayName = 'MoveItem'

interface NotationControlsProps {
    gameTree: GameTree
    currentPosition: GameTreePosition
    disabled: boolean
    setCurrentPosition: (nextPosition: GameTreePosition) => void
}

const NotationControls: FC<NotationControlsProps> = memo((props) => {
    const { gameTree, currentPosition, setCurrentPosition, disabled } = props

    const hasNextMoves = currentPosition.nextMoveIds.length > 0
    const hasPrevMoves = currentPosition.previousMoveId !== undefined
    const prevDisabled = !hasPrevMoves || disabled
    const nextDisabled = !hasNextMoves || disabled

    const onFirstMove = useCallback(() => {
        const position = getStartingPosition(gameTree)
        setCurrentPosition(position)
    }, [gameTree])

    const onBackMove = useCallback(() => {
        const position = getPreviousPosition(gameTree, currentPosition.id)
        if (!position) return
        setCurrentPosition(position)
    }, [gameTree, currentPosition.id])

    const onNextMove = useCallback(() => {
        const position = getNextPosition(gameTree, currentPosition.id)
        if (!position) return
        setCurrentPosition(position)
    }, [gameTree, currentPosition.id])

    const onLastMove = useCallback(() => {
        const position = getLastPosition(gameTree)
        setCurrentPosition(position)
    }, [gameTree])

    return (
        <Stack direction="row" justifyContent="center" gap="12px">
            <CircularButton
                onClick={onFirstMove}
                title="First"
                disabled={prevDisabled}
                removeBorder
                icon={<FirstIcon />}
            />
            <CircularButton
                onClick={onBackMove}
                title="Back"
                disabled={prevDisabled}
                removeBorder
                icon={<BackIcon />}
            />
            <CircularButton
                onClick={onNextMove}
                title="Next"
                disabled={nextDisabled}
                removeBorder
                icon={<NextIcon />}
            />
            <CircularButton
                onClick={onLastMove}
                title="Last"
                disabled={nextDisabled}
                removeBorder
                icon={<LastIcon />}
            />
        </Stack>
    )
})
NotationControls.displayName = 'NotationControls'

export const Notations: FC<NotationProps> = ({
    gameTree,
    setCurrentPosition,
    currentPosition,
    notations,
    disabled,
}) => {
    const scrollRef = useRef<HTMLDivElement>(null)

    const currentPositionIsLast = useMemo(
        () => currentPosition.id === notations[notations.length - 1]?.nextPositionId,
        [currentPosition.id, notations],
    )

    // Auto-scroll to show newest moves when notations change
    useEffect(() => {
        const container = scrollRef.current

        if (container) {
            setTimeout(() => container.firstElementChild?.scrollIntoView({ behavior: 'smooth' }), 50)
        }
    }, [notations.length])

    const handlePositionChange = useCallback(
        (move: NotationsType) => {
            if (!disabled) {
                const nextPosition = getPositionById(gameTree, move.nextPositionId)
                setCurrentPosition(nextPosition)
            }
        },
        [disabled, gameTree],
    )

    // Group moves: show dots at start, then pair bot move + player move
    const movePairs = useMemo(() => {
        const pairs: Array<{ white?: NotationsType; black?: NotationsType; moveNumber: number; showDots?: boolean }> =
            []

        let moveCounter = 1

        // Always show dots at the start as first row (even if no notations)
        const firstBotMove = notations.find((m) => !m.playersMove)
        if (firstBotMove) {
            pairs.push({ black: firstBotMove, moveNumber: moveCounter, showDots: true })
        } else if (notations.length === 0) {
            // Show dots as fallback when no notations
            pairs.push({ moveNumber: moveCounter, showDots: true })
        }

        notations.forEach((move) => {
            // Skip first bot move since it's already added with dots
            if (!move.playersMove && pairs.length === 1 && pairs[0]?.black === move) {
                return
            }

            if (move.playersMove) {
                // Player move on left - start new row
                moveCounter++
                pairs.push({ white: move, moveNumber: moveCounter })
            } else {
                // Bot move on right - add to last row
                const lastPair = pairs[pairs.length - 1]
                if (lastPair && !lastPair.black) {
                    lastPair.black = move
                }
            }
        })

        // Descending sort for result
        return pairs.sort((a, b) => b.moveNumber - a.moveNumber)
    }, [notations])

    const isActive = useCallback(
        (move: NotationsType) => move.nextPositionId === currentPosition.id && !currentPositionIsLast,
        [currentPosition.id, currentPositionIsLast],
    )

    return (
        <Stack
            sx={{
                width: '100%',
                px: { xs: '8px', md: '15%' },
                py: 1,
                flexGrow: 1,
                minHeight: 0,
                overflow: 'hidden',
                border: '2px solid',
                borderColor: 'primary.text',
                borderRadius: '8px !important',
                backgroundColor: 'background.default',
                gap: '8px',
            }}
        >
            <Stack alignItems="center">
                <Typography variant="h5" textTransform="uppercase">
                    Move List
                </Typography>
            </Stack>
            <Stack
                ref={scrollRef}
                direction="column"
                gap="4px"
                sx={{
                    overflow: 'auto',
                    minHeight: { xs: '300px', md: 0 },
                    flex: '1 1 0',
                }}
            >
                {movePairs.map((pair, pairIndex) => (
                    <Stack key={pairIndex} direction="row" alignItems="center" gap="4px">
                        <MoveNumber number={pair.moveNumber} />
                        <Stack justifyContent="space-between" flexGrow={1} direction="row">
                            {pair.white ? (
                                <MoveItem
                                    move={pair.white}
                                    isActive={isActive(pair.white)}
                                    disabled={disabled}
                                    onClick={() => handlePositionChange(pair.white!)}
                                />
                            ) : pair.showDots ? (
                                <Typography lineHeight="48px" fontSize="16px" fontWeight="bold" variant="body2">
                                    ...
                                </Typography>
                            ) : (
                                <Box />
                            )}

                            {pair.black && (
                                <MoveItem
                                    move={pair.black}
                                    isActive={isActive(pair.black)}
                                    disabled={disabled}
                                    onClick={() => handlePositionChange(pair.black!)}
                                />
                            )}
                        </Stack>
                    </Stack>
                ))}
            </Stack>
            <NotationControls
                setCurrentPosition={setCurrentPosition}
                gameTree={gameTree}
                currentPosition={currentPosition}
                disabled={disabled}
            />
        </Stack>
    )
}
